import { FC } from 'react'
import moment from 'moment'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import styles from './trendBadge.module.scss'

interface TrendBadgeProps {
  className?: string
  // Text to show instead of "<n>%", e.g. "1 new source". The arrow and colour
  // still follow `value`.
  label?: string
  // For metrics where a rise is bad (e.g. response time): the arrow still
  // points the way the number moved, but a rise is drawn red and a fall green.
  lowerIsBetter?: boolean
  // The period the value is compared against, shown on hover so the badge
  // says what it compares to.
  previousFromDate?: string
  previousToDate?: string
  // % change vs the previous period. null / undefined means there is nothing
  // to compare against (lifetime, or an empty previous period) — that is not a
  // change of 0, so the badge renders nothing.
  value: number | null | undefined
}

export const TrendBadge: FC<TrendBadgeProps> = ({
  className,
  label,
  lowerIsBetter = false,
  previousFromDate,
  previousToDate,
  value,
}) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null
  }

  const isFlat = value === 0
  const isUp = value > 0
  const isGood = lowerIsBetter ? !isUp : isUp
  const tone = isFlat ? styles.flat : isGood ? styles.good : styles.bad
  const hint =
    previousFromDate && previousToDate
      ? `vs previous period: ${moment(previousFromDate).format(
          'MMM D, YYYY'
        )} – ${moment(previousToDate).format('MMM D, YYYY')}`
      : 'vs previous period'

  return (
    <span className={`${styles.badge} ${tone} ${className ?? ''}`} title={hint}>
      {!isFlat && (
        <TrendUpIcon
          aria-hidden="true"
          className={`${styles.icon} ${isUp ? '' : styles.iconDown}`}
        />
      )}
      <span className={styles.value}>{label ?? `${Math.abs(value)}%`}</span>
    </span>
  )
}
